import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import axios from '@/utils/axios';
import { useAuth } from '@/context/AuthContext';
import { apiErrorMessage } from '@/utils/apiError';
import { MAX_DISPLAY_NAME_LENGTH, displayNameErrorKey, suggestedDisplayName } from '@/utils/personName';
import { KEY_PREFIX, isPromptDue } from '@/utils/displayNamePrompt';
import { safeLocalStorage } from '@/utils/safeStorage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * Asks a member whose profile predates the required name to choose one (owner
 * decision 2026-11-02, "soft"). Mirrors mobile `display_name_prompt.dart`.
 *
 * Soft on purpose: "Not now" always works and nothing waits on an answer; the
 * other members see her described («فتاة، ٢٨، القاهرة») meanwhile. It comes
 * back at most every three days (utils/displayNamePrompt.js) and stops once a name is saved. Save sends
 * `display_name_public: true` because the dialog SAYS the name is shown to
 * others (backend services/member_label.py).
 *
 * Only on the pages a member lands on - never over a form she is filling in
 * or a conversation.
 */
const LANDING_PATHS = ['/browse', '/dashboard'];

// Blocked site data: the prompt may simply come back sooner.
const readLastShown = (userId) => safeLocalStorage.getItem(KEY_PREFIX + userId);
const writeLastShown = (userId) => safeLocalStorage.setItem(KEY_PREFIX + userId, new Date().toISOString());

const DisplayNamePrompt = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const [formId, setFormId] = useState(null);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const onLandingPage = LANDING_PATHS.includes(location.pathname);
  const userId = user?.id;

  useEffect(() => {
    if (!isAuthenticated || !userId || !onLandingPage) return undefined;
    if (!isPromptDue(readLastShown(userId))) return undefined;
    let cancelled = false;
    axios.get('/forms/my-forms')
      .then((res) => {
        if (cancelled) return;
        const form = (res.data?.forms || []).find((f) => f.status !== 'deleted');
        if (!form || (typeof form.display_name === 'string' && form.display_name.trim())) return;
        writeLastShown(userId);
        setName(suggestedDisplayName(user?.name) || '');
        setFormId(form.id);
      })
      .catch(() => {
        // A prompt is never worth an error on the page she opened.
      });
    return () => { cancelled = true; };
  // `user.name` only seeds the field; it must not re-run the check.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, userId, onLandingPage]);

  const close = () => {
    if (saving) return;
    setFormId(null);
    setError('');
  };

  const save = async () => {
    const value = name.trim();
    const errorKey = displayNameErrorKey(value);
    if (errorKey) {
      setError(t(errorKey));
      return;
    }
    setSaving(true);
    setError('');
    try {
      await axios.put(`/forms/${formId}`, { display_name: value, display_name_public: true });
      setFormId(null);
      toast.success(t('displayNamePrompt.saved'));
    } catch (e) {
      setError(apiErrorMessage(e, t('form.errorCheckAllFields')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(formId)} onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('displayNamePrompt.title')}</DialogTitle>
          <DialogDescription className="pt-2 leading-relaxed">
            {t('displayNamePrompt.body')}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => { e.preventDefault(); save(); }}
          className="space-y-2"
        >
          <label htmlFor="display-name-prompt" className="text-sm font-medium text-wefaqna-navy">
            {t('form.displayNameLabel')}
          </label>
          <Input
            id="display-name-prompt"
            value={name}
            maxLength={MAX_DISPLAY_NAME_LENGTH}
            autoComplete="nickname"
            dir="auto"
            aria-invalid={Boolean(error)}
            aria-describedby={error ? 'display-name-prompt-error' : undefined}
            onChange={(e) => { setName(e.target.value); if (error) setError(''); }}
          />
          {error && (
            <p id="display-name-prompt-error" role="alert" className="text-sm text-red-600">{error}</p>
          )}
          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" className="min-h-[44px]" disabled={saving} onClick={close}>
              {t('displayNamePrompt.later')}
            </Button>
            <Button type="submit" className="min-h-[44px] bg-wefaqna-forest text-white" disabled={saving}>
              {t('displayNamePrompt.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DisplayNamePrompt;
